import { WebLayout } from 'components/Layout';
import React from 'react';
import AboutBanner from 'assets/about-banner.png';
import LonnahAvatar from 'assets/team/lonnah.png';
import JonahAvatar from 'assets/team/jonah.png';
import QoutesSvg from 'assets/qoutes.svg';
import { motion } from 'framer-motion';

export const AboutUs = () => {
  return (
    <WebLayout>
      {/* HERO SECTION */}
      <section className="px-6 md:px-32 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* LEFT TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <h1 className="text-5xl md:text-7xl font-extrabold mb-4 text-[#0a0a0a]">
              About <span className="text-[#F3C72C]">Us.</span>
            </h1>

            <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
              We build secure, intelligent systems for Africa’s agricultural transformation.
            </h2>

            <div className="text-gray-700 space-y-5 leading-relaxed text-lg">
              <p>
                Ashita is a technology company dedicated to empowering farmers, cooperatives, and
                agribusinesses with secure, intelligent digital systems.
              </p>

              <p>
                Our mission is simple: help agriculture operate smarter, safer, and more efficiently
                through modern digital infrastructure engineered with deep contextual understanding.
              </p>

              <p>
                We combine engineering, research, and design — creating tools that honor culture,
                empower communities, and respect the complexities of agricultural life.
              </p>

              <p>
                The word <strong>“Ashita”</strong> means <em>“tomorrow.”</em> It represents our
                commitment to building infrastructure that strengthens the future of food systems
                and rural economies.
              </p>
            </div>
          </motion.div>

          {/* RIGHT BANNER */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="flex justify-center md:justify-end"
          >
            <img
              src={AboutBanner}
              alt="About Us Banner"
              className="h-64 md:h-[26vw] object-contain rounded-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="px-6 md:px-24 py-20 mx-4 md:mx-14 rounded-3xl bg-[#0a0a0a] border border-[#F3C72C]/10 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
        {/* Title Badge */}
        <div className="text-center mb-12">
          <div className="inline-block px-10 py-2 rounded-full border border-[#F3C72C] bg-[#0a0a0a]">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#F3C72C]">
              Managed By A Young Bold Team
            </h2>
          </div>

          <p className="mt-8 text-gray-300 text-lg md:text-xl max-w-3xl mx-auto">
            We're engineers, designers, cybersecurity experts, and agriculture-focused innovators
            building digital infrastructure for Africa’s future.
          </p>
        </div>

        {/* TEAM GRID */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 items-start md:items-center">
          {/* LONNAH */}
          <div className="flex flex-col items-center">
            <img
              src={LonnahAvatar}
              alt="Lonnah"
              className="h-80 object-cover rounded-xl grayscale hover:grayscale-0 transition duration-300"
            />

            <div className="text-white text-2xl mt-4 text-center font-bold">
              Lonnah Alinda Tumwesigye
            </div>

            <div className="text-center mt-2 px-4 py-1 border border-[#F3C72C] rounded-xl text-[#F3C72C] text-sm font-semibold">
              COO, Operations
            </div>

            <div className="text-gray-400 text-sm mt-2 text-center">Cybersecurity Expert</div>
          </div>

          {/* JONATHAN */}
          <div className="flex flex-col items-center">
            <img
              src={JonahAvatar}
              alt="Jonathan"
              className="h-80 object-cover rounded-xl grayscale hover:grayscale-0 transition duration-300"
            />

            <div className="text-white text-2xl mt-4 text-center font-bold">Jonathan Mwebaze</div>

            <div className="text-center mt-2 px-4 py-1 border border-[#F3C72C] rounded-xl text-[#F3C72C] text-sm font-semibold">
              Director & CEO
            </div>

            <div className="text-gray-400 text-sm mt-2 text-center">
              Systems Architecture & DevOps Specialist
            </div>
          </div>

          {/* QUOTE */}
          <div className="md:col-span-2 md:ml-16 mt-8 md:mt-0">
            <img src={QoutesSvg} alt="Quote icon" className="h-10 opacity-50 mb-4" />

            <p className="text-gray-300 text-xl leading-relaxed">
              “At Ashita, we're crafting the digital backbone that agriculture deserves — secure,
              reliable, and built with deep respect for the people who feed nations. The future of
              farming depends on intelligent systems, and we’re committed to building them.”
            </p>

            <div className="text-right text-[#F3C72C] mt-4 text-sm md:mr-4">
              – Jonathan, Director
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="px-6 md:px-32 py-20">
        {/* Services Badge */}
        <div className="inline-block px-4 py-1 rounded-full border border-[#F3C72C] text-[#F3C72C] text-sm mb-6">
          Our Services
        </div>

        {/* Services Heading */}
        <h2 className="text-2xl md:text-3xl font-extrabold text-[#0a0a0a] mb-12 max-w-2xl">
          We build digital systems that strengthen agricultural businesses and rural economies.
        </h2>

        {/* New Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              title: 'Agritech Infrastructure & Digital Farm Systems',
              body: 'Farm management systems, operational intelligence, data collection tools, and automation.',
            },
            {
              title: 'Fintech Architecture for Farmer Communities',
              body: 'Savings groups, shared wallets, loan logic, secure identity layers, and farmer financial rails.',
            },
            {
              title: 'E-commerce & Produce Market Platforms',
              body: 'Modern marketplaces that help farmers sell directly and efficiently.',
            },
            {
              title: 'Security-first System Design',
              body: 'Zero-trust architecture, encrypted data flows, secure authentication, and identity protection.',
            },
            {
              title: 'Product Strategy & Prototyping',
              body: 'Rapid prototyping, UX/UI design, and digital product validation.',
            },
          ].map((service, i) => (
            <div
              key={i}
              className="border border-[#F3C72C]/20 rounded-2xl p-8 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.05)] 
              hover:border-[#F3C72C] hover:shadow-[0_6px_24px_rgba(0,0,0,0.12)] transition-all cursor-pointer"
            >
              <div className="h-3 w-3 rounded-full bg-[#F3C72C] mb-4"></div>

              <h3 className="text-xl font-bold text-[#0a0a0a] mb-2">{service.title}</h3>

              <p className="text-gray-700 leading-relaxed">{service.body}</p>
            </div>
          ))}
        </div>

        {/* Contact */}
        <p className="mt-16 text-lg text-[#0a0a0a]">
          Interested in working with us? Reach out at{' '}
          <a
            href="mailto:ashitacreatives@gmail.com"
            className="text-[#F3C72C] underline hover:text-[#d1a500]"
          >
            ashitacreatives@gmail.com
          </a>
        </p>
      </section>
    </WebLayout>
  );
};
